// src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { THEMES, ThemeDefinition } from "../data/themes";
import { isDevelopment } from "../extensions/environments";

const OVERRIDE_STORAGE_KEY = "theme-override";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns true if `date` falls within [start, end] (inclusive).
 * Handles year-wrap ranges (e.g. Dec 27 → Jan 2).
 */
function isDateInRange(
  date: Date,
  start: { month: number; day: number },
  end: { month: number; day: number },
): boolean {
  const startVal = start.month * 100 + start.day;
  const endVal = end.month * 100 + end.day;
  const nowVal = (date.getMonth() + 1) * 100 + date.getDate();

  if (startVal > endVal) {
    // Range wraps the year boundary (e.g. Dec 27 – Jan 2)
    return nowVal >= startVal || nowVal <= endVal;
  }
  return nowVal >= startVal && nowVal <= endVal;
}

/**
 * Resolves the active ThemeDefinition based on:
 *   1. A manual override (localStorage key or ?theme= URL param)
 *   2. The first theme in THEMES whose date range includes `now`
 *   3. The last entry in THEMES (americana — the permanent fallback)
 */
function resolveTheme(override: string | null, now: Date): ThemeDefinition {
  if (override) {
    const found = THEMES.find((t) => t.id === override);
    if (found) return found;
  }

  for (const theme of THEMES) {
    if (theme.startDate && theme.endDate) {
      // Skip year-specific themes when the calendar year doesn't match
      if (theme.year !== undefined && now.getFullYear() !== theme.year)
        continue;
      if (isDateInRange(now, theme.startDate, theme.endDate)) {
        return theme;
      }
    }
  }

  return THEMES[THEMES.length - 1];
}

/** Reads the initial override from URL param or localStorage.
 * Override via ?theme= or localStorage is only honoured in local development.
 * In production the date-window logic is the sole activation mechanism.
 */
function getInitialOverride(): string | null {
  if (!isDevelopment()) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const param = params.get("theme");
    if (param) return param;
    return localStorage.getItem(OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface ThemeContextValue {
  /** The currently active theme definition. */
  activeTheme: ThemeDefinition;
  /**
   * Manually pin a specific theme by ID.
   * Persists to localStorage so the override survives page reloads.
   */
  setThemeOverride: (themeId: string) => void;
  /**
   * Remove the manual override, reverting to date-based auto-selection.
   * Clears localStorage and removes the URL param (if present).
   */
  clearThemeOverride: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [override, setOverride] = useState<string | null>(getInitialOverride);
  const activeTheme = resolveTheme(override, new Date());

  // Apply data-theme attribute to <html> so [data-theme="..."] CSS rules fire
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", activeTheme.id);
  }, [activeTheme.id]);

  const setThemeOverride = (themeId: string) => {
    try {
      localStorage.setItem(OVERRIDE_STORAGE_KEY, themeId);
    } catch {
      // localStorage unavailable (private browsing, storage full, etc.)
    }
    setOverride(themeId);
  };

  const clearThemeOverride = () => {
    try {
      localStorage.removeItem(OVERRIDE_STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
    setOverride(null);
  };

  return (
    <ThemeContext.Provider
      value={{ activeTheme, setThemeOverride, clearThemeOverride }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
